import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import { TimelineActionRow } from "./TimelineTaskComponents";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import { EggFreezingClinicTimelineStep } from "./EggFreezingPrepTimelineRemainingSteps";

type PreCycleCareVariant =
  "embryo-freezing" | "ivf" | "iui" | "amab-family-building";

function buildCareComparisonCopy(t: TranslationFunction): Record<
  PreCycleCareVariant,
  {
    title: string;
    subtitle: string;
    icon: "clinics" | "process" | "sperm";
    action: PlanAction;
  }
> {
  return {
    "embryo-freezing": {
      title: t("homepage.review_concept.embryo_clinic_compare_title"),
      subtitle: t("homepage.review_concept.embryo_clinic_compare_subtitle"),
      icon: "clinics",
      action: {
        label: t("homepage.review_concept.embryo_clinic_compare_title"),
        screen: "FindCareScreen",
        params: { filter: "embryo-freezing", mode: "compare" },
      },
    },
    ivf: {
      title: t("homepage.review_concept.ivf_clinic_compare_title"),
      subtitle: t("homepage.review_concept.ivf_clinic_compare_subtitle"),
      icon: "clinics",
      action: {
        label: t("homepage.review_concept.ivf_clinic_compare_cta"),
        screen: "FindCareScreen",
        params: { filter: "ivf", mode: "compare" },
      },
    },
    iui: {
      title: t("homepage.review_concept.iui_clinic_compare_title"),
      subtitle: t("homepage.review_concept.iui_clinic_compare_subtitle"),
      icon: "process",
      action: {
        label: t("homepage.review_concept.iui_clinic_compare_cta"),
        screen: "FindCareScreen",
        params: { filter: "iui", mode: "compare" },
      },
    },
    "amab-family-building": {
      title: t("homepage.review_concept.amab_clinic_compare_title"),
      subtitle: t("homepage.review_concept.amab_clinic_compare_subtitle"),
      icon: "sperm",
      action: {
        label: t("homepage.review_concept.amab_clinic_compare_cta"),
        screen: "FindCareScreen",
        params: { filter: "amab-family-building", mode: "compare" },
      },
    },
  };
}

export function FindEmbryoCareStep({
  onAction,
  variant = "embryo-freezing",
}: {
  onAction: (action: PlanAction) => void;
  variant?: PreCycleCareVariant;
}) {
  const t = useTranslations();
  const copy = buildCareComparisonCopy(t)[variant];

  return (
    <EggFreezingClinicTimelineStep onAction={onAction} variant={variant}>
      <TimelineActionRow
        title={copy.title}
        subtitle={copy.subtitle}
        icon={<OrganicMiniIcon kind={copy.icon} size={34} />}
        onPress={() => onAction(copy.action)}
      />
    </EggFreezingClinicTimelineStep>
  );
}
